import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react';
import useAuthStore from '../../store/authStore';

const VerifyPhonePage = () => {
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes in seconds
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  
  const { verifyPhone, resendVerification, isLoading, error, user } = useAuthStore();

  // Get phone from store or redirect
  const phone = user?.phone || '';

  useEffect(() => {
    if (!phone) {
      navigate('/register');
    }
  }, [phone, navigate]);

  useEffect(() => {
    if (timeLeft <= 0) return;

    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [timeLeft]);

  const handleChange = (index, value) => {
    if (!/^\d?$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      document.getElementById(`digit-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      document.getElementById(`digit-${index - 1}`).focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(paste)) {
      const newCode = paste.split('').concat(Array(6 - paste.length).fill(''));
      setCode(newCode);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const verificationCode = code.join('');
    
    if (verificationCode.length !== 6) {
      return;
    }

    try {
      await verifyPhone({ phone, code: verificationCode });
      navigate('/dashboard');
    } catch (error) {
      console.error('Verification error:', error);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    try {
      await resendVerification(phone);
      setTimeLeft(600); // Reset timer
      setCode(['', '', '', '', '', '']);
      document.getElementById('digit-0').focus();
    } catch (error) {
      console.error('Resend error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary-500/30">
          <Phone className="h-10 w-10 text-white" />
        </div>
        <div className="flex items-center justify-center mb-2">
          <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h2 className="text-xl font-bold text-gray-800">Verify Your Phone</h2>
        </div>
        <p className="text-sm text-gray-500">
          We sent a 6-digit code to <span className="font-semibold text-primary-600">{phone}</span>
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Enter the code below to verify your phone number
        </p>
      </div>

      {error && (
        <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
          <div className="flex items-start">
            <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h3 className="text-xs font-medium text-red-800">Error</h3>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-3 text-center">
            Verification Code
          </label>
          <div className="flex justify-center space-x-3">
            {code.map((digit, index) => (
              <input
                key={index}
                id={`digit-${index}`}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className="w-14 h-14 text-center text-2xl font-semibold bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-colors"
                autoFocus={index === 0}
              />
            ))}
          </div>
        </div>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Code expires in{' '}
            <span className={`font-semibold ${timeLeft < 60 ? 'text-red-600' : 'text-primary-600'}`}>
              {formatTime(timeLeft)}
            </span>
          </p>
        </div>

        <div className="space-y-3">
          <button
            type="submit"
            disabled={isLoading || code.join('').length !== 6}
            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-3 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Verifying...
              </div>
            ) : (
              'Verify Phone Number'
            )}
          </button>

          <button
            type="button"
            onClick={handleResendCode}
            disabled={isResending || timeLeft > 540} // Can resend after 1 minute
            className="w-full bg-white rounded-lg px-4 py-2.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium text-gray-700 border border-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isResending ? 'animate-spin' : ''}`} />
            {isResending ? 'Resending...' : 'Resend Code'}
            {timeLeft > 540 && ` (${formatTime(timeLeft - 540)})`}
          </button>
        </div>
      </form>

      <div className="text-center">
        <button
          onClick={() => navigate('/dashboard')}
          className="text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset"
        >
          Verify later
        </button>
      </div>

      <div className="bg-blue-50 rounded-xl p-4 shadow-neomorph-inset border border-blue-200/50">
        <div className="flex items-start">
          <div className="p-1.5 bg-blue-100 rounded-md shadow-neomorph-inset mr-3">
            <CheckCircle className="h-4 w-4 text-blue-600" />
          </div>
          <div>
            <h3 className="text-xs font-medium text-blue-800 mb-2">Why verify your phone?</h3>
            <ul className="text-xs text-blue-700 space-y-1 list-disc pl-4">
              <li>Secure your account</li>
              <li>Receive transaction notifications</li>
              <li>Enable faster password recovery</li>
              <li>Required for some transactions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerifyPhonePage;